<template>
  <div class="login-container">
    <v-row class="fill-height m-0">
      <v-col
        md="7"
        class="d-flex justify-center align-center left-panel"
        v-if="this.$vuetify.breakpoint.smAndUp"
      >
        <div class="left-panel-content">
          <img src="../assets/logo1.png" alt="Policía San Juan" class="main-logo" />
          <h2 class="text-h4 font-weight-bold text-center mb-6">Sistema de Pedidos Captura vehicular Provincial</h2>
          <LottieFiles :width="500" :options="defaultOptions" />
        </div>
      </v-col>
      <v-col
        cols="12"
        md="5"
        class="right-panel"
      >
        <v-card class="login-card" elevation="2" rounded="lg">
          <v-card-text class="text-center pt-6">
            <img src="../assets/logo1.png" alt="Policía San Juan" class="card-logo mb-4" />
            <h4 class="text-h5 primary--text font-weight-bold mb-2">
              Acceso al Sistema
            </h4>
            <p class="subtitle-2 grey--text text--darken-1 mb-4">Ingrese sus credenciales para continuar</p>
          </v-card-text>

          <v-form ref="form" action v-model="valid" lazy-validation class="px-6">
            <v-card-text>
              <!-- Campo de usuario con validación -->
              <v-text-field
                v-model="usuario"
                :rules="usuarioRules"
                label="Usuario"
                prepend-inner-icon="mdi-account"
                required
                outlined
                dense
                color="primary"
                class="input-field mb-4"
                @keyup.enter="submit"
              ></v-text-field>

              <!-- Campo de contraseña con validación y opción para mostrar/ocultar -->
              <v-text-field
                v-model="password"
                :type="verContraseña ? 'text' : 'password'"
                :rules="passwordRules"
                label="Contraseña"
                prepend-inner-icon="mdi-key"
                :append-icon="verContraseña ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append="verContraseña = !verContraseña"
                required
                outlined
                dense
                color="primary"
                class="input-field mb-4"
                @keyup.enter="submit"
              ></v-text-field>
              
              <!-- Mensaje de error si hay problemas de autenticación -->
              <v-alert
                v-if="errorMessage"
                type="error"
                dense
                text
                class="mb-4"
              >
                {{ errorMessage }}
              </v-alert>
            </v-card-text>

            <v-card-actions class="d-flex flex-column gap px-6 pb-6">
              <!-- Botón de ingreso principal -->
              <v-btn
                rounded
                :loading="loading"
                :disabled="loadingGoogle || !valid"
                block
                color="primary"
                @click="submit"
                height="44"
                class="login-btn"
                elevation="1"
              >
                <v-icon left> mdi-login </v-icon>
                <span class="font-weight-bold">INGRESAR AL SISTEMA</span>
              </v-btn>
              
              <!-- Botón de ingreso con Google (desactivado en esta implementación) -->
              <v-btn
                class="mt-4 google-btn"
                rounded
                :loading="loadingGoogle"
                :disabled="loading || !googleLoginEnabled"
                block
                color="error"
                @click="loginGoogle"
                height="44"
                elevation="1"
              >
                <v-icon left> mdi-google </v-icon>
                <span class="font-weight-bold">INGRESAR CON GOOGLE</span>
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
        
        <v-card
          class="help-card mt-6"
          max-width="450"
          rounded="lg"
          elevation="2"
        >
          <v-list-item three-line>
            <v-list-item-content>
              <div class="text-overline mb-2 primary--text font-weight-bold">SOPORTE TÉCNICO</div>
              <v-list-item-subtitle class="subtitle-1 mb-2">
                ¿Necesitas ayuda? Contáctanos por WhatsApp para asistencia inmediata
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-avatar
              :size="$vuetify.breakpoint.smAndUp ? 60 : 50"
              class="support-avatar"
            >
              <v-img
                class="elevation-2"
                alt=""
                src="https://avataaars.io/?avatarStyle=Transparent&topType=ShortHairShortCurly&accessoriesType=Prescription02&hairColor=Black&facialHairType=Blank&clotheType=Hoodie&clotheColor=White&eyeType=Default&eyebrowType=DefaultNatural&mouthType=Default&skinColor=Light"
              ></v-img>
            </v-list-item-avatar>
          </v-list-item>

          <v-card-actions class="pb-4 px-4">
            <v-btn rounded block color="success" @click="msjWhatsapp()" class="whatsapp-btn" elevation="1" height="44">
              <span class="font-weight-bold">ENVIAR MENSAJE</span>
              <v-icon right dark> mdi-whatsapp </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
        
        <div class="copyright-text mt-6">
          &copy; {{ new Date().getFullYear() }} Sistema de Estadísticas Policiales. Todos los derechos reservados.
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import * as animationData from "../assets/lottiefiles/login.json";

export default {
  name: 'LoginPage',
  data() {
    return {
      defaultOptions: {
        animationData: animationData.default,
      },
      loading: false,
      loadingGoogle: false,
      valid: false,
      verContraseña: false,
      password: "",
      usuario: "",
      errorMessage: "",
      googleLoginEnabled: false, // Desactivado por defecto según la implementación actual
      usuarioRules: [
        v => !!v || "El nombre de usuario es obligatorio",
        v => v.length >= 3 || "El usuario debe tener al menos 3 caracteres"
      ],
      passwordRules: [
        v => !!v || "La contraseña es obligatoria",
        v => v.length >= 6 || "La contraseña debe tener al menos 6 caracteres"
      ],
    };
  },
  created() {
    // Verificar si hay un token guardado y redirigir si ya está autenticado
    if (this.$store.getters['user/isAuthenticated']) {
      // Usar catch para manejar posibles errores de navegación redundante
      this.$router.push({ name: 'home' }).catch(err => {
        // Ignorar errores de navegación redundante
        if (err.name !== 'NavigationDuplicated') {
          console.error('Error de navegación:', err);
        }
      });
    }
  },
  methods: {
    /**
     * Método para enviar las credenciales y autenticar al usuario
     * Utiliza el store para realizar la autenticación
     */
    async submit() {
      // Limpiar mensaje de error previo
      this.errorMessage = "";
      
      // Validar el formulario
      if (this.$refs.form.validate()) {
        this.loading = true;

        try {
          // Llamar a la acción de login en el store
          await this.$store.dispatch("user/login", {
            username: this.usuario,
            password: this.password,
          });
          
          // Si llegamos aquí, el login fue exitoso
          // La redirección se maneja en el store
        } catch (error) {
          // Manejar errores de autenticación
          console.error("Error de autenticación:", error);
          this.errorMessage = error.message || "Error al intentar iniciar sesión. Verifique sus credenciales.";
        } finally {
          this.loading = false;
        }
      }
    },
    
    /**
     * Método para autenticar con Google (actualmente no implementado)
     */
    async loginGoogle() {
      this.loadingGoogle = true;
      this.errorMessage = "";

      try {
        await this.$store.dispatch("user/login", {
          loginGoogle: true,
        });
      } catch (error) {
        console.error("Error en login con Google:", error);
        this.errorMessage = error.message || "Error al intentar iniciar sesión con Google.";
      } finally {
        this.loadingGoogle = false;
      }
    },
    
    /**
     * Método para enviar mensaje de WhatsApp al soporte técnico
     */
    msjWhatsapp() {
      const phoneNumber = "542644446909";
      const message = "Buenos días, tengo la siguiente novedad con el sistema";
      const encodedMessage = encodeURIComponent(message);
      const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

      window.location.href = whatsappURL;
    },
  },
};
</script>

<style>
@import 'https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css';

.login-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%);
  overflow: hidden;
}

.left-panel {
  background: linear-gradient(45deg, #0d47a1, #1565c0, #0d47a1);
  background-size: 400% 400%;
  animation: gradientBG 15s ease infinite;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

@keyframes gradientBG {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.left-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 2px, transparent 2px, transparent 4px);
  opacity: 0.3;
}

.left-panel-content {
  position: relative;
  z-index: 1;
  max-width: 80%;
  text-align: center;
  color: white;
}

.main-logo {
  max-width: 300px;
  margin-bottom: 2rem;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.right-panel {
  height: 100vh;
  background-color: #f5f7fa;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.login-card {
  width: 90%;
  max-width: 450px;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.card-logo {
  max-width: 180px;
  height: auto;
}

.help-card {
  width: 90%;
  max-width: 450px;
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.input-field {
  margin-bottom: 1rem;
}

.login-btn {
  background-color: #1976d2 !important;
  text-transform: none;
}

.google-btn {
  background-color: #f44336 !important;
  text-transform: none;
}

.whatsapp-btn {
  background-color: #4caf50 !important;
  text-transform: none;
}

.support-avatar {
  border: 2px solid #fff;
}

.copyright-text {
  font-size: 12px;
  color: #9e9e9e;
  text-align: center;
  margin-top: 2rem;
}

.gap {
  gap: 12px;
}

/* Responsive */
@media (max-width: 600px) {
  .login-card, .help-card {
    width: 95%;
    margin-top: 20px;
  }
  
  .right-panel {
    padding: 1.5rem;
    height: auto;
    min-height: 100vh;
  }
  
  .card-logo {
    max-width: 150px;
  }
}
</style>
